import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { flushSync } from 'react-dom';
import { useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { User } from '../modules/platform/types';
import { getCurrentUser } from '../modules/platform/api/users';
import {
  bumpAuthSessionEpoch,
  clearAuthSessionStorage,
  getAuthSessionEpoch,
  markAuthSessionLoggedOut,
  refreshAccessTokenSingleFlight,
  resetLogoutRedirectState,
} from '../utils/authSession';
import { getAccessToken, getRefreshToken, clearTokens, setTokens, isTokenExpired } from '../utils/token';
import { clearIdentityHeaders } from '../utils/apiHeaders';
import { clearInteractiveBookChatOnSignOut } from '../modules/interactive-book/runtime/interactiveBookChatSession';
import * as authApi from '../modules/platform/api/auth';
import type { LoginOptions } from '../modules/platform/api/auth';
import type { LoginResponse } from '../modules/platform/types';
import { ROSTER_STUDENT_ME_QUERY_KEY } from '../modules/roster/queryKeys';
import { LEARNER_CURRICULUM_ROOT } from '../modules/curriculum/queryKeys';
import { applyRoleHeadersFromUser } from '../auth/roleHeaders';
import {
  hasValidAccessToken,
  subscribeAccessTokenState,
} from '../utils/accessTokenState';
import { preservePortalForLogout } from '../utils/loginPortal';
import { clearPublicTenantIntent } from '../utils/publicTenantHandoff';

/** Dedupe concurrent boot-time loads (e.g. React Strict Mode). */
let loadUserInFlight: Promise<User | null> | null = null;
/** Avoid spamming console when /me returns 401 once per session. */
let authMeUnauthorizedWarned = false;
interface AuthContextType {
  user: User | null;
  loading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string, options?: LoginOptions) => Promise<User>;
  /** Exchange LMS assertion tokens and load full session (same path as login). */
  completeLmsSession: (response: LoginResponse) => Promise<User>;
  register: (email: string, password: string, role: 'PARENT' | 'STUDENT') => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  hasRole: (role: string) => boolean;
  hasAnyRole: (roles: string[]) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    // Fallback to a safe default to avoid crashing during HMR or unexpected render order.
    // This prevents "useAuth must be used within AuthProvider" runtime exceptions.
    // Consumers should still behave as unauthenticated when provider is missing.
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    const noop = async () => {};
    return {
      user: null,
      loading: false,
      isAuthenticated: false,
      login: async (_email: string, _password: string, _options?: LoginOptions) => {
        return Promise.reject(new Error('AuthProvider missing'));
      },
      completeLmsSession: async (_response: LoginResponse) => {
        return Promise.reject(new Error('AuthProvider missing'));
      },
      register: async (_email: string, _password: string, _role: 'PARENT' | 'STUDENT') => {
        return Promise.reject(new Error('AuthProvider missing'));
      },
      logout: async () => {
        return Promise.resolve();
      },
      refreshUser: async () => {
        return Promise.resolve();
      },
      hasRole: (_role: string) => false,
      hasAnyRole: (_roles: string[]) => false,
    } as AuthContextType;
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const queryClient = useQueryClient();
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const authBootstrapGenRef = useRef(0);

  const loadUser = async (): Promise<User | null> => {
    if (loadUserInFlight) {
      return loadUserInFlight;
    }

    const epochAtStart = getAuthSessionEpoch();

    loadUserInFlight = (async (): Promise<User | null> => {
      const token = getAccessToken();
      if (!token) {
        if (epochAtStart === getAuthSessionEpoch()) {
          setUser(null);
        }
        return null;
      }

      if (isTokenExpired(token)) {
        const refreshed = await refreshAccessTokenSingleFlight();
        if (!refreshed) {
          preservePortalForLogout({
            pathname: typeof window !== 'undefined' ? window.location.pathname : undefined,
          });
          clearTokens();
          clearIdentityHeaders();
          if (epochAtStart === getAuthSessionEpoch()) {
            setUser(null);
          }
          return null;
        }
      }

      try {
        let lastError: unknown;
        for (let attempt = 0; attempt < 3; attempt += 1) {
          try {
            const data = await getCurrentUser();
            if (epochAtStart !== getAuthSessionEpoch()) {
              return null;
            }
            setUser(data.user);
            applyRoleHeadersFromUser(data.user);
            return data.user;
          } catch (error) {
            lastError = error;
            const isNetworkError =
              axios.isAxiosError(error) &&
              (!error.response || error.code === 'ERR_NETWORK' || error.message.includes('Network Error'));
            if (isNetworkError && attempt < 2) {
              await new Promise((resolve) => setTimeout(resolve, 500 * (attempt + 1)));
              continue;
            }
            throw error;
          }
        }
        throw lastError;
      } catch (error) {
        if (epochAtStart !== getAuthSessionEpoch()) {
          return null;
        }
        if (axios.isAxiosError(error) && error.response?.status === 401) {
          if (!authMeUnauthorizedWarned) {
            authMeUnauthorizedWarned = true;
            console.warn('Session invalid or expired (GET /me returned 401). Clearing auth and identity headers.');
          }
          preservePortalForLogout({
            pathname: typeof window !== 'undefined' ? window.location.pathname : undefined,
          });
          clearTokens();
          clearIdentityHeaders();
          setUser(null);
          return null;
        }
        console.error('Failed to load user:', error);
        preservePortalForLogout({
          pathname: typeof window !== 'undefined' ? window.location.pathname : undefined,
        });
        clearTokens();
        setUser(null);
        return null;
      }
    })().finally(() => {
      loadUserInFlight = null;
    });

    return loadUserInFlight;
  };

  useEffect(() => {
    const gen = ++authBootstrapGenRef.current;
    void (async () => {
      await loadUser();
      if (authBootstrapGenRef.current === gen) {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    return subscribeAccessTokenState(() => {
      if (!hasValidAccessToken()) {
        if (import.meta.env.DEV) {
          console.info('[auth] access token cleared or expired — resetting session user');
        }
        setUser(null);
        setLoading(false);
        clearIdentityHeaders();
      }
    });
  }, []);

  const login = async (email: string, password: string, options?: LoginOptions): Promise<User> => {
    // Allow auth endpoints after manual logout (markAuthSessionLoggedOut blocks axios until reset).
    resetLogoutRedirectState();
    queryClient.removeQueries({ queryKey: ROSTER_STUDENT_ME_QUERY_KEY });
    queryClient.removeQueries({ queryKey: [LEARNER_CURRICULUM_ROOT] });
    if (import.meta.env.DEV) {
      console.log('[auth] Calling login API');
    }
    const response = await authApi.login({
      email,
      password,
      ...(options?.expectedRole ? { expected_role: options.expectedRole } : {}),
      ...(options?.loginPortal === 'admin' ? { login_portal: 'admin' } : {}),
    });
    setTokens(response.access_token, response.refresh_token);
    const data = await getCurrentUser();
    flushSync(() => {
      setUser(data.user);
      applyRoleHeadersFromUser(data.user);
    });
    return data.user;
  };

  const completeLmsSession = async (response: LoginResponse): Promise<User> => {
    setTokens(response.access_token, response.refresh_token);
    queryClient.removeQueries({ queryKey: ROSTER_STUDENT_ME_QUERY_KEY });
    queryClient.removeQueries({ queryKey: [LEARNER_CURRICULUM_ROOT] });
    const token = getAccessToken();
    if (!token) {
      throw new Error('Failed to store session tokens');
    }
    const data = await getCurrentUser();
    setUser(data.user);
    try {
      const primaryRole = data.user.roles?.[0];
      const tenantId = data.user.memberships?.[0]?.school_id || '';
      if (primaryRole) localStorage.setItem('X_ROLE', primaryRole);
      if (tenantId) localStorage.setItem('X_TENANT_ID', tenantId);
      if (data.user.id) localStorage.setItem('X_USER_ID', data.user.id);
    } catch {
      // ignore localStorage errors
    }
    setLoading(false);
    return data.user;
  };

  const register = async (email: string, password: string, role: 'PARENT' | 'STUDENT') => {
    const response = await authApi.register({ email, password, role });
    setTokens(response.access_token, response.refresh_token);
    await loadUser();
  };

  const logout = async () => {
    if (import.meta.env.DEV) {
      console.log('[auth] Logout cleanup started');
    }
    bumpAuthSessionEpoch();
    loadUserInFlight = null;
    authMeUnauthorizedWarned = false;

    const refreshToken = getRefreshToken();

    // Preserve portal before clearing so Header / expiry can restore the right login shell.
    preservePortalForLogout({
      pathname: typeof window !== 'undefined' ? window.location.pathname : undefined,
    });

    // Clear client session before awaiting the API so stale /me cannot re-auth.
    clearInteractiveBookChatOnSignOut();
    clearAuthSessionStorage();
    clearIdentityHeaders();
    clearPublicTenantIntent();
    markAuthSessionLoggedOut();
    setUser(null);
    setLoading(false);
    queryClient.removeQueries({ queryKey: ROSTER_STUDENT_ME_QUERY_KEY });
    queryClient.removeQueries({ queryKey: [LEARNER_CURRICULUM_ROOT] });

    if (refreshToken) {
      try {
        await authApi.logout(refreshToken);
      } catch (error) {
        console.error('Logout error:', error);
      }
    }

    // User is heading to /login — re-enable unauthenticated auth API calls (login/register).
    resetLogoutRedirectState();
    if (import.meta.env.DEV) {
      console.log('[auth] Logout cleanup completed', {
        accessToken: getAccessToken(),
        refreshToken: getRefreshToken(),
        user: null,
      });
    }
  };

  const refreshUser = async () => {
    await loadUser();
  };

  const hasRole = (role: string): boolean => {
    if (!user) return false;
    return user.roles.includes(role as any);
  };

  const hasAnyRole = (roles: string[]): boolean => {
    if (!user) return false;
    const normalized = user.roles.map((r) => String(r).toUpperCase());
    return roles.some((role) => normalized.includes(String(role).toUpperCase()));
  };

  const isAuthenticated = !!user;

  const value: AuthContextType = {
    user,
    loading,
    isAuthenticated,
    login,
    completeLmsSession,
    register,
    logout,
    refreshUser,
    hasRole,
    hasAnyRole,
  };

  return (
    <AuthContext.Provider value={value}>
      <div
        data-testid={loading ? 'auth-loading' : 'auth-ready'}
        data-auth-authenticated={isAuthenticated ? 'true' : 'false'}
        hidden
        aria-hidden="true"
      />
      {children}
    </AuthContext.Provider>
  );
};

